// apps/web/src/routes/turfs/$turfSlug.tsx
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { captureException, track } from '@turfchale/analytics'
import {
  deriveDayNightPrice,
  hasAcceptedCurrent,
  recordAcceptance,
  resolvePrice,
  supabase,
  useAuthStore,
} from '@turfchale/supabase'
import { addDays, format, isPast, isToday, parseISO } from 'date-fns'
import { ArrowLeft, Clock, IndianRupee, Loader, MapPin, Star } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'

import { Logo } from '@/components/ui/Logo'
import { loadRazorpay, type RazorpayHandlerResponse } from '@/lib/razorpay'
import { PLAYER_AGREEMENT_VERSION } from '@/routes/legal/player-agreement'

export const Route = createFileRoute('/turfs/$turfSlug')({
  component: TurfDetailPage,
})

type DateOption = 'today' | 'tomorrow' | { label: string; value: string }

interface Slot {
  date: string
  end_time: string
  id: string
  price: null | number
  start_time: string
  status: 'blocked' | 'booked' | 'held' | 'open'
  turf_id: string
}

interface TurfDetail {
  address: string
  amenities: Record<string, boolean>
  avg_rating: number
  city: string
  close_time: null | string
  cover_image_url: null | string
  deposit_amount: number
  description: null | string
  id: string
  image_urls: string[]
  is_24x7: boolean | null
  max_booking_hours: number
  name: string
  open_time: null | string
  price_per_hour: number
  sport_types: string[]
  status: string
  total_reviews: number
}

const DATE_OPTIONS: DateOption[] = [
  'today',
  'tomorrow',
  {
    label: format(addDays(new Date(), 2), 'EEE d MMM'),
    value: format(addDays(new Date(), 2), 'yyyy-MM-dd'),
  },
  {
    label: format(addDays(new Date(), 3), 'EEE d MMM'),
    value: format(addDays(new Date(), 3), 'yyyy-MM-dd'),
  },
  {
    label: format(addDays(new Date(), 4), 'EEE d MMM'),
    value: format(addDays(new Date(), 4), 'yyyy-MM-dd'),
  },
  {
    label: format(addDays(new Date(), 5), 'EEE d MMM'),
    value: format(addDays(new Date(), 5), 'yyyy-MM-dd'),
  },
  {
    label: format(addDays(new Date(), 6), 'EEE d MMM'),
    value: format(addDays(new Date(), 6), 'yyyy-MM-dd'),
  },
]

const SPORT_LABELS: Record<string, string> = {
  badminton: '🏸 Badminton',
  basketball: '🏀 Basketball',
  cricket: '🏏 Cricket',
  football: '⚽ Football',
  other: '🎯 Other',
  pickleball: '🏓 Pickleball',
  tennis: '🎾 Tennis',
  volleyball: '🏐 Volleyball',
}

const SPORT_EMOJI: Record<string, string> = {
  badminton: '🏸',
  basketball: '🏀',
  cricket: '🏏',
  football: '⚽',
  other: '🎯',
  pickleball: '🏓',
  tennis: '🎾',
  volleyball: '🏐',
}

const AMENITY_LABELS: { key: string; label: string }[] = [
  { key: 'parking', label: '🅿️ Parking' },
  { key: 'floodlights', label: '💡 Floodlit' },
  { key: 'changing_room', label: '🚿 Changing Room' },
  { key: 'water', label: '💧 Water' },
  { key: 'cafeteria', label: '☕ Cafeteria' },
]

function DetailPriceTier({ label, price }: { label: string; price: number }) {
  return (
    <div>
      <div
        style={{
          color: 'var(--text-dim)',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.08em',
          marginBottom: 2,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <div
        style={{
          alignItems: 'baseline',
          display: 'flex',
          fontFamily: 'Clash Display',
          fontSize: 24,
          fontWeight: 700,
          gap: 2,
        }}
      >
        <IndianRupee size={18} />
        {price.toLocaleString('en-IN')}
        <span style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 400 }}>/hour</span>
      </div>
    </div>
  )
}

function endTimeOf(time: string): string {
  const h = Number(time.split(':')[0]) + 1
  return `${h.toString().padStart(2, '0')}:00:00`
}

function getDateLabel(opt: DateOption): string {
  if (opt === 'today') return 'Today'
  if (opt === 'tomorrow') return 'Tomorrow'
  return opt.label
}

function getDateValue(opt: DateOption): string {
  if (opt === 'today') return format(new Date(), 'yyyy-MM-dd')
  if (opt === 'tomorrow') return format(addDays(new Date(), 1), 'yyyy-MM-dd')
  return opt.value
}

function ImageCarousel({ images, turfName }: { images: string[]; turfName: string }) {
  const [current, setCurrent] = useState(0)
  const [touchStart, setTouchStart] = useState<null | number>(null)

  if (images.length === 0) return null

  const prev = () => setCurrent((c) => (c === 0 ? images.length - 1 : c - 1))
  const next = () => setCurrent((c) => (c === images.length - 1 ? 0 : c + 1))

  const handleTouchStart = (e: React.TouchEvent) => setTouchStart(e.touches[0].clientX)
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return
    const diff = touchStart - e.changedTouches[0].clientX
    if (Math.abs(diff) > 50) {
      if (diff > 0) next()
      else prev()
    }
    setTouchStart(null)
  }

  const showControls = images.length > 1

  return (
    <div
      onTouchEnd={handleTouchEnd}
      onTouchStart={handleTouchStart}
      style={{
        height: 260,
        overflow: 'hidden',
        position: 'relative',
        userSelect: 'none',
      }}
    >
      {/* Images track */}
      <div
        style={{
          display: 'flex',
          height: '100%',
          transform: `translateX(-${current * 100}%)`,
          transition: 'transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        }}
      >
        {images.map((url, i) => (
          <img
            alt={`${turfName} photo ${i + 1}`}
            key={i}
            src={url}
            style={{
              display: 'block',
              flexShrink: 0,
              height: '100%',
              objectFit: 'cover',
              width: '100%',
            }}
          />
        ))}
      </div>

      {/* Overlay gradient at bottom for indicator legibility */}
      {showControls && (
        <div
          style={{
            background: 'linear-gradient(transparent 50%, rgba(0,0,0,0.6))',
            bottom: 0,
            height: 64,
            left: 0,
            pointerEvents: 'none',
            position: 'absolute',
            right: 0,
          }}
        />
      )}

      {/* Arrow buttons */}
      {showControls && (
        <>
          <button
            aria-label="Previous image"
            onClick={prev}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0,0,0,0.7)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(0,0,0,0.45)')}
            style={{
              background: 'rgba(0,0,0,0.45)',
              border: 'none',
              borderRadius: '0 8px 8px 0',
              color: '#fff',
              cursor: 'pointer',
              fontSize: 18,
              left: 0,
              lineHeight: 1,
              padding: '10px 10px 10px 8px',
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              transition: 'background 0.15s',
            }}
          >
            ‹
          </button>
          <button
            aria-label="Next image"
            onClick={next}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0,0,0,0.7)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(0,0,0,0.45)')}
            style={{
              background: 'rgba(0,0,0,0.45)',
              border: 'none',
              borderRadius: '8px 0 0 8px',
              color: '#fff',
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
              padding: '10px 8px 10px 10px',
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              transition: 'background 0.15s',
            }}
          >
            ›
          </button>
        </>
      )}

      {/* Dot indicators */}
      {showControls && (
        <div
          style={{
            bottom: 10,
            display: 'flex',
            gap: 6,
            justifyContent: 'center',
            left: 0,
            position: 'absolute',
            right: 0,
            zIndex: 2,
          }}
        >
          {images.map((_, i) => (
            <button
              aria-label={`View photo ${i + 1}`}
              key={i}
              onClick={() => setCurrent(i)}
              style={{
                background: i === current ? 'var(--lime)' : 'rgba(255,255,255,0.5)',
                border: 'none',
                borderRadius: '50%',
                cursor: 'pointer',
                height: 8,
                padding: 0,
                transition: 'all 0.2s',
                width: i === current ? 22 : 8,
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 6,
      }}
    >
      <span style={{ color: 'var(--muted)' }}>{label}</span>
      <span style={{ fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  )
}

function TurfDetailPage() {
  const { turfSlug } = Route.useParams()
  const navigate = useNavigate()
  const profile = useAuthStore((s) => s.profile)
  const [selectedDate, setSelectedDate] = useState<DateOption>('today')
  const [selectedSlots, setSelectedSlots] = useState<string[]>([])
  const [booking, setBooking] = useState(false)
  const [needsAcceptance, setNeedsAcceptance] = useState(false)
  const [agreed, setAgreed] = useState(false)

  useEffect(() => {
    let active = true
    if (profile) {
      hasAcceptedCurrent('player', PLAYER_AGREEMENT_VERSION).then((accepted) => {
        if (active) {
          setNeedsAcceptance(!accepted)
          setAgreed(false)
        }
      })
    } else {
      // Reset when user signs out
      Promise.resolve().then(() => {
        if (active) {
          setNeedsAcceptance(false)
          setAgreed(false)
        }
      })
    }
    return () => {
      active = false
    }
  }, [profile])

  const { data: turf, isLoading: turfLoading } = useQuery({
    queryFn: async () => {
      const { data, error } = await supabase
        .from('turfs')
        .select(
          'id, name, address, city, cover_image_url, image_urls, price_per_hour, deposit_amount, sport_types, avg_rating, total_reviews, description, amenities, max_booking_hours, open_time, close_time, is_24x7',
        )
        .eq('slug', turfSlug)
        .single()
      if (error) throw error
      return data as TurfDetail
    },
    queryKey: ['turf', turfSlug],
  })

  // Real UUID resolved from the slug-loaded row; all downstream turf_id refs use this.
  const turfId = turf?.id ?? ''

  useEffect(() => {
    if (!turf) return
    track('turf_viewed', {
      city: turf.city,
      sport: turf.sport_types?.[0] ?? 'unknown',
      turf_id: turf.id,
      turf_name: turf.name,
    })
    // Fire once per distinct turf; keyed on id so it doesn't re-fire on refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turf?.id])

  const selectedDateStr = getDateValue(selectedDate)

  const { data: slots = [], isLoading: slotsLoading } = useQuery({
    enabled: !!turf,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('slots')
        .select('*')
        .eq('turf_id', turfId)
        .eq('date', selectedDateStr)
        .order('start_time')
      if (error) throw error
      return data as Slot[]
    },
    queryKey: ['slots', turfId, selectedDateStr],
  })

  const queryClient = useQueryClient()

  const { data: pricingRows = [] } = useQuery({
    enabled: !!turf,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('turf_pricing_schedule')
        .select('day_of_week, hour, price')
        .eq('turf_id', turfId)
      if (error) throw error
      return data as { day_of_week: number; hour: number; price: number }[]
    },
    queryKey: ['pricing', turfId],
  })

  const templateMap = useMemo(
    () => new Map(pricingRows.map((r) => [`${r.day_of_week}-${r.hour}`, r.price])),
    [pricingRows],
  )

  const dayNight = useMemo(
    () => deriveDayNightPrice(pricingRows, turf?.price_per_hour ?? 0),
    [pricingRows, turf?.price_per_hour],
  )

  const allSlots = useMemo(() => {
    const slotMap = new Map(slots.map((s) => [s.start_time, s]))
    // Grid spans the turf's operating hours (slot start times). Midnight close
    // (00:00) means end-of-day → 24; an end <= start is treated as overnight.
    const openH = parseInt(turf?.open_time?.slice(0, 2) ?? '6', 10)
    const rawClose = parseInt(turf?.close_time?.slice(0, 2) ?? '23', 10)
    let start = openH
    let end = rawClose === 0 ? 24 : rawClose
    if (turf?.is_24x7) {
      start = 0
      end = 24
    } else if (end <= start) {
      end += 24 // overnight (e.g. opens 6pm, closes 2am)
    }
    const result: { dbSlot: null | Slot; time: string }[] = []
    for (let h = start; h < end; h++) {
      const time = `${(h % 24).toString().padStart(2, '0')}:00:00`
      result.push({ dbSlot: slotMap.get(time) ?? null, time })
    }
    return result
  }, [slots, turf?.open_time, turf?.close_time, turf?.is_24x7])

  function getSlotStatus(time: string) {
    const s = allSlots.find((a) => a.time === time)
    if (!s?.dbSlot) return 'available'
    if (s.dbSlot.status === 'booked') return 'booked'
    if (s.dbSlot.status === 'held') return 'held'
    if (s.dbSlot.status === 'blocked') return 'blocked'
    return 'open'
  }

  function getSlotPrice(time: string) {
    const s = allSlots.find((a) => a.time === time)
    const dow = parseISO(selectedDateStr).getDay()
    const hour = Number(time.split(':')[0])
    const templatePrice = templateMap.get(`${dow}-${hour}`) ?? null
    return resolvePrice(s?.dbSlot?.price ?? null, templatePrice, turf?.price_per_hour ?? 0)
  }

  function trackSlotSelected(time: string) {
    track('slot_selected', {
      price: getSlotPrice(time),
      slot_id: time,
      sport: turf?.sport_types?.[0] ?? 'unknown',
      start_time: time,
      turf_id: turfId,
    })
  }

  function toggleSlot(time: string) {
    const sel = selectedSlots
    if (sel.length === 0) {
      setSelectedSlots([time])
      trackSlotSelected(time)
      return
    }
    const firstH = Number(sel[0].split(':')[0])
    const lastH = Number(sel[sel.length - 1].split(':')[0])
    const h = Number(time.split(':')[0])

    // Shrink from either end
    if (h === firstH) {
      setSelectedSlots(sel.slice(1))
      track('slot_deselected', { slot_id: time, turf_id: turfId })
      return
    }
    if (h === lastH) {
      setSelectedSlots(sel.slice(0, -1))
      track('slot_deselected', { slot_id: time, turf_id: turfId })
      return
    }

    // Extend by one adjacent hour
    if (h === firstH - 1 || h === lastH + 1) {
      const max = turf?.max_booking_hours ?? 4
      if (sel.length >= max) {
        toast(`Up to ${max} hr${max > 1 ? 's' : ''} per booking`)
        return
      }
      setSelectedSlots(h === firstH - 1 ? [time, ...sel] : [...sel, time])
      trackSlotSelected(time)
      return
    }

    // Non-adjacent
    toast('Pick a continuous block')
  }

  async function handleConfirmBooking() {
    if (selectedSlots.length === 0 || !turf) return
    if (!profile) {
      navigate({ search: { redirect: `/turfs/${turfSlug}` }, to: '/auth' })
      return
    }
    if (needsAcceptance && !agreed) return

    setBooking(true)

    if (needsAcceptance) {
      await recordAcceptance('player', PLAYER_AGREEMENT_VERSION)
    }

    const scriptOk = await loadRazorpay()
    if (!scriptOk) {
      toast.error('Could not load payment. Check your connection.')
      setBooking(false)
      return
    }

    const sport = turf.sport_types?.[0] ?? 'unknown'
    const holdSlotId = selectedSlots.join(',')
    const holdPrice = selectedSlots.reduce((sum, t) => sum + getSlotPrice(t), 0)

    track('slot_hold_initiated', { price: holdPrice, slot_id: holdSlotId, turf_id: turfId })

    const { data, error } = await supabase.functions.invoke('create-booking-order', {
      body: { date: selectedDateStr, start_times: selectedSlots, turf_id: turfId },
    })

    if (error) {
      setBooking(false)
      track('slot_hold_failed', { reason: 'unavailable', slot_id: holdSlotId, turf_id: turfId })
      captureException(error, { context: 'create_booking_order', turf_id: turfId })
      const msg = (error as { context?: { error?: string } }).context?.error
      if (msg === 'turf_not_bookable') {
        toast.error('This turf is not yet accepting bookings. Please check back soon.')
      } else {
        toast.error('Failed to create order. Please try again.')
      }
      queryClient.invalidateQueries({ queryKey: ['slots', turfId, selectedDateStr] })
      return
    }
    if (!data?.order_id) {
      setBooking(false)
      track('slot_hold_failed', { reason: 'unavailable', slot_id: holdSlotId, turf_id: turfId })
      toast.error('Could not start payment. A slot may have just been taken.')
      queryClient.invalidateQueries({ queryKey: ['slots', turfId, selectedDateStr] })
      return
    }

    track('slot_hold_succeeded', {
      hold_expires_at: data.hold_expires_at ?? '',
      slot_id: holdSlotId,
      turf_id: turfId,
    })

    const bookingId = String(data.booking_id ?? data.order_id)

    const rzp = new window.Razorpay({
      amount: data.amount,
      currency: 'INR',
      description: turf.name,
      handler: async (resp: RazorpayHandlerResponse) => {
        const { error: verifyError } = await supabase.functions.invoke('verify-payment', {
          body: {
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          },
        })
        setBooking(false)
        if (verifyError) {
          track('payment_failed', { amount: data.amount, booking_id: bookingId })
          captureException(verifyError, { context: 'verify_payment', turf_id: turfId })
          toast('Payment received — confirming your booking shortly.')
        } else {
          track('payment_succeeded', {
            amount: data.amount,
            booking_id: bookingId,
            sport,
            turf_id: turfId,
          })
          toast.success('Booking confirmed!')
        }
        navigate({ to: '/app' })
      },
      key: data.key_id,
      modal: {
        ondismiss: () => {
          track('payment_cancelled', { amount: data.amount, booking_id: bookingId })
          setBooking(false)
        },
      },
      name: 'TurfChale',
      order_id: data.order_id,
      prefill: { email: profile.email ?? undefined },
      theme: { color: '#b8ec3c' },
    })

    track('checkout_viewed', { amount: data.amount, slot_id: holdSlotId, turf_id: turfId })
    track('payment_initiated', { amount: data.amount, booking_id: bookingId, turf_id: turfId })
    rzp.open()
  }

  // Build carousel images array: cover_image first, then gallery image_urls
  const carouselImages = useMemo(() => {
    const imgs: string[] = []
    if (turf?.cover_image_url) imgs.push(turf.cover_image_url)
    if (turf?.image_urls?.length) {
      turf.image_urls.forEach((url) => {
        if (url !== turf.cover_image_url) imgs.push(url)
      })
    }
    return imgs
  }, [turf])

  if (turfLoading) {
    return (
      <div
        style={{
          alignItems: 'center',
          background: 'var(--bg)',
          color: 'var(--text)',
          display: 'flex',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Loader className="animate-spin" size={24} />
      </div>
    )
  }

  if (!turf) {
    return (
      <div
        style={{
          alignItems: 'center',
          background: 'var(--bg)',
          color: 'var(--muted)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <MapPin size={32} />
        <div>Turf not found</div>
        <button
          onClick={() => navigate({ to: '/turfs' })}
          style={{
            background: 'var(--lime)',
            border: 'none',
            borderRadius: 8,
            color: 'var(--bg)',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 600,
            padding: '8px 16px',
          }}
        >
          Browse turfs
        </button>
      </div>
    )
  }

  const firstSport = turf.sport_types?.[0]
  const playFee = selectedSlots.reduce((sum, t) => sum + getSlotPrice(t), 0)
  const deposit = turf.deposit_amount ?? 0
  const total = playFee + deposit
  const hasSelection = selectedSlots.length > 0
  const canBook = hasSelection && !booking && (!needsAcceptance || agreed)
  const blockStart = hasSelection ? selectedSlots[0] : null
  const blockEnd = hasSelection ? endTimeOf(selectedSlots[selectedSlots.length - 1]) : null
  const blockHours = selectedSlots.length
  const activeAmenities = AMENITY_LABELS.filter(({ key }) => turf.amenities?.[key])

  let ctaLabel = 'Select slots to continue'
  if (hasSelection && !profile) ctaLabel = 'Sign in to book'
  if (hasSelection && profile && !booking)
    ctaLabel = `Confirm Booking · ${blockHours} hr${blockHours > 1 ? 's' : ''}`
  if (booking) ctaLabel = 'Booking…'

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh' }}>
      {/* Top bar */}
      <div
        style={{
          alignItems: 'center',
          background: 'var(--bg2)',
          borderBottom: '1px solid var(--lime-line)',
          display: 'flex',
          padding: '12px 16px',
        }}
      >
        <button
          onClick={() => navigate({ to: '/turfs' })}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text)',
            cursor: 'pointer',
            display: 'flex',
            padding: 4,
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <div style={{ fontSize: 14, fontWeight: 600, marginLeft: 8 }}>Book a Turf</div>
        <div style={{ marginLeft: 'auto' }}>
          <Logo fontSize={16} />
        </div>
      </div>

      {/* Image carousel */}
      {carouselImages.length > 0 ? (
        <ImageCarousel images={carouselImages} turfName={turf.name} />
      ) : (
        <div
          style={{
            alignItems: 'center',
            background: 'linear-gradient(135deg, var(--bg2) 0%, var(--bg) 100%)',
            display: 'flex',
            fontSize: 48,
            height: 200,
            justifyContent: 'center',
          }}
        >
          {firstSport ? (SPORT_EMOJI[firstSport] ?? '📍') : '📍'}
        </div>
      )}

      <div style={{ margin: '0 auto', maxWidth: 1040, padding: '20px 16px' }}>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start lg:gap-10">
          {/* Left column: turf info, date, slots */}
          <div>
            {/* Turf info */}
            <div style={{ marginBottom: 24 }}>
              <h1
                style={{
                  fontFamily: 'Clash Display',
                  fontSize: 22,
                  fontWeight: 700,
                  margin: '0 0 4px',
                }}
              >
                {turf.name}
              </h1>
              <div
                style={{
                  alignItems: 'center',
                  color: 'var(--muted)',
                  display: 'flex',
                  fontSize: 13,
                  gap: 4,
                  marginBottom: 8,
                }}
              >
                <MapPin size={13} />
                {turf.address}, {turf.city}
              </div>

              {/* Sport tags */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                {turf.sport_types?.map((s) => (
                  <span
                    key={s}
                    style={{
                      background: 'var(--lime-dim)',
                      borderRadius: 4,
                      color: 'var(--lime)',
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '2px 7px',
                    }}
                  >
                    {SPORT_LABELS[s] ?? s}
                  </span>
                ))}
              </div>

              {/* Rating */}
              {turf.total_reviews > 0 && (
                <div style={{ alignItems: 'center', display: 'flex', gap: 6, marginBottom: 8 }}>
                  <Star size={14} style={{ color: 'var(--lime)', fill: 'var(--lime)' }} />
                  <span style={{ color: 'var(--lime)', fontSize: 13, fontWeight: 600 }}>
                    {turf.avg_rating.toFixed(1)}
                  </span>
                  <span style={{ color: 'var(--muted)', fontSize: 12 }}>
                    ({turf.total_reviews} reviews)
                  </span>
                </div>
              )}

              {/* Description */}
              {turf.description && (
                <div
                  style={{
                    color: 'var(--muted)',
                    fontSize: 13,
                    lineHeight: 1.5,
                    marginBottom: 8,
                  }}
                >
                  {turf.description}
                </div>
              )}

              {/* Amenities */}
              {activeAmenities.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {activeAmenities.map(({ key, label }) => (
                    <span
                      key={key}
                      style={{
                        background: 'var(--bg3)',
                        borderRadius: 4,
                        color: 'var(--text-dim)',
                        fontSize: 11,
                        padding: '2px 8px',
                      }}
                    >
                      {label}
                    </span>
                  ))}
                </div>
              )}

              {/* Price */}
              {dayNight.flat ? (
                <div
                  style={{
                    alignItems: 'baseline',
                    display: 'flex',
                    fontFamily: 'Clash Display',
                    fontSize: 24,
                    fontWeight: 700,
                    gap: 2,
                    marginTop: 8,
                  }}
                >
                  <IndianRupee size={18} />
                  {dayNight.day.toLocaleString('en-IN')}
                  <span style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 400 }}>
                    /hour
                  </span>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
                  <DetailPriceTier label="Day" price={dayNight.day} />
                  <DetailPriceTier label="Night" price={dayNight.night} />
                </div>
              )}
            </div>

            {/* Date selector */}
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  color: 'var(--text-dim)',
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  marginBottom: 8,
                  textTransform: 'uppercase',
                }}
              >
                Select date
              </div>
              <div style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }}>
                {DATE_OPTIONS.map((opt) => {
                  const value = getDateValue(opt)
                  const label = getDateLabel(opt)
                  const isSelected = selectedDateStr === value
                  return (
                    <button
                      key={value}
                      onClick={() => {
                        setSelectedDate(opt)
                        setSelectedSlots([])
                        track('date_selected', { date: value, turf_id: turfId })
                      }}
                      style={{
                        background: isSelected ? 'var(--lime)' : 'var(--bg3)',
                        border: '1px solid var(--lime-line)',
                        borderRadius: 8,
                        color: isSelected ? 'var(--bg)' : 'var(--text)',
                        cursor: 'pointer',
                        flexShrink: 0,
                        fontSize: 12,
                        fontWeight: isSelected ? 700 : 500,
                        padding: '8px 14px',
                        transition: 'all 0.12s',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Slots */}
            <div style={{ marginBottom: 24 }}>
              <div
                style={{
                  alignItems: 'center',
                  color: 'var(--text-dim)',
                  display: 'flex',
                  fontSize: 11,
                  fontWeight: 600,
                  gap: 8,
                  letterSpacing: '0.08em',
                  marginBottom: 8,
                  textTransform: 'uppercase',
                }}
              >
                <Clock size={13} />
                Available slots
              </div>

              {slotsLoading && (
                <div
                  style={{ color: 'var(--muted)', fontSize: 13, padding: 16, textAlign: 'center' }}
                >
                  Loading slots…
                </div>
              )}

              {!slotsLoading && (
                <div
                  style={{
                    display: 'grid',
                    gap: 6,
                    gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                  }}
                >
                  {allSlots.map(({ time }) => {
                    const status = getSlotStatus(time)
                    const isSelected = selectedSlots.includes(time)
                    const isPastTime =
                      isToday(parseISO(selectedDateStr)) &&
                      isPast(parseISO(`${selectedDateStr}T${time}`))
                    const disabled =
                      status === 'booked' || status === 'held' || status === 'blocked' || isPastTime

                    return (
                      <button
                        disabled={disabled}
                        key={time}
                        onClick={() => !disabled && toggleSlot(time)}
                        style={{
                          background: disabled
                            ? 'var(--bg2)'
                            : isSelected
                              ? 'var(--lime)'
                              : 'var(--bg3)',
                          border: `1px solid ${disabled ? 'rgba(255,255,255,0.06)' : isSelected ? 'var(--lime)' : 'var(--lime-line)'}`,
                          borderRadius: 8,
                          color: disabled
                            ? 'var(--text-dim)'
                            : isSelected
                              ? 'var(--bg)'
                              : 'var(--lime)',
                          cursor: disabled ? 'default' : 'pointer',
                          fontSize: 12,
                          fontWeight: isSelected ? 700 : 500,
                          opacity: disabled ? 0.4 : 1,
                          padding: '10px 4px',
                          textAlign: 'center',
                          textDecoration: disabled ? 'line-through' : 'none',
                          transition: 'all 0.12s',
                        }}
                      >
                        {format(parseISO(`2000-01-01T${time}`), 'h:mm a')}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right column: booking summary (sticky on desktop) */}
          <div
            className="lg:sticky lg:top-6"
            style={{
              background: 'var(--bg2)',
              border: '1px solid var(--lime-line)',
              borderRadius: 12,
              padding: 16,
            }}
          >
            <div
              style={{
                color: 'var(--text-dim)',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.08em',
                marginBottom: 12,
                textTransform: 'uppercase',
              }}
            >
              Booking summary
            </div>

            <div
              style={{
                borderBottom: '1px solid var(--lime-line)',
                fontSize: 13,
                paddingBottom: 12,
              }}
            >
              <SummaryRow label="Turf" value={turf.name} />
              <SummaryRow label="Date" value={format(parseISO(selectedDateStr), 'MMM d, yyyy')} />
              <SummaryRow
                label="Time"
                value={
                  blockStart && blockEnd
                    ? `${format(parseISO(`2000-01-01T${blockStart}`), 'h:mm a')} – ${format(parseISO(`2000-01-01T${blockEnd}`), 'h:mm a')} (${blockHours} hr${blockHours > 1 ? 's' : ''})`
                    : '—'
                }
              />
            </div>

            <div style={{ fontSize: 13, paddingTop: 12 }}>
              <SummaryRow
                label={`Slots (${blockHours} hr${blockHours > 1 ? 's' : ''})`}
                value={hasSelection ? `₹${playFee.toLocaleString('en-IN')}` : '—'}
              />
              {deposit > 0 && (
                <SummaryRow label="Deposit" value={`₹${deposit.toLocaleString('en-IN')}`} />
              )}
              <SummaryRow label="Platform fee" value="Free" />
              <div
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  fontFamily: 'Clash Display',
                  fontSize: 18,
                  fontWeight: 700,
                  justifyContent: 'space-between',
                  marginTop: 8,
                }}
              >
                <span>Total</span>
                <span style={{ color: 'var(--lime)' }}>
                  {hasSelection ? `₹${total.toLocaleString('en-IN')}` : '—'}
                </span>
              </div>
            </div>

            {hasSelection && profile && needsAcceptance && (
              <label
                style={{
                  alignItems: 'flex-start',
                  color: 'var(--text-dim)',
                  cursor: 'pointer',
                  display: 'flex',
                  fontSize: 12.5,
                  gap: 9,
                  lineHeight: 1.5,
                  marginTop: 14,
                }}
              >
                <input
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  style={{ accentColor: 'var(--lime)', marginTop: 2 }}
                  type="checkbox"
                />
                <span>
                  I agree to the{' '}
                  <a
                    href="/legal/player-agreement"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--lime)' }}
                    target="_blank"
                  >
                    Player Agreement
                  </a>
                  .
                </span>
              </label>
            )}

            <button
              disabled={!canBook}
              onClick={handleConfirmBooking}
              style={{
                background: canBook ? 'var(--lime)' : 'var(--bg3)',
                border: 'none',
                borderRadius: 10,
                color: canBook ? 'var(--bg)' : 'var(--text-dim)',
                cursor: canBook ? 'pointer' : 'default',
                fontSize: 15,
                fontWeight: 700,
                marginTop: 16,
                padding: '14px 20px',
                width: '100%',
              }}
            >
              {ctaLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
